import { useEffect } from "react";
import { resolveSeo } from "@/utilities/seo";

/**
 * Lightweight SEO hook — manipulates document.head directly so we don't need
 * to add react-helmet-async as a dependency for a JS-only CRA build.
 * NOTE: For production-grade SEO of dynamic pages, migrate to Next.js SSR.
 */
function upsertMeta(selector, attrs) {
    if (typeof document === "undefined") return;
    let el = document.head.querySelector(selector);
    if (!el) {
        el = document.createElement("meta");
        Object.entries(attrs).forEach(([k, v]) => {
            if (k !== "content") el.setAttribute(k, v);
        });
        document.head.appendChild(el);
    }
    if (attrs.content !== undefined) el.setAttribute("content", attrs.content);
}

function upsertLink(rel, href) {
    if (typeof document === "undefined") return;
    let el = document.head.querySelector(`link[rel="${rel}"]`);
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
}

export function useSeo(seo, path) {
    useEffect(() => {
        const resolved = resolveSeo(seo || {}, path || (typeof window !== "undefined" ? window.location.pathname : "/"));
        document.title = resolved.title;

        upsertMeta('meta[name="description"]', { name: "description", content: resolved.description });
        if (resolved.keywords?.length) {
            upsertMeta('meta[name="keywords"]', { name: "keywords", content: resolved.keywords.join(", ") });
        }
        upsertMeta('meta[name="robots"]', { name: "robots", content: resolved.robots || "index,follow" });

        upsertLink("canonical", resolved.canonicalUrl);

        upsertMeta('meta[property="og:title"]', { property: "og:title", content: resolved.ogTitle });
        upsertMeta('meta[property="og:description"]', { property: "og:description", content: resolved.ogDescription });
        upsertMeta('meta[property="og:image"]', { property: "og:image", content: resolved.ogImage });
        upsertMeta('meta[property="og:url"]', { property: "og:url", content: resolved.canonicalUrl });
        upsertMeta('meta[property="og:type"]', { property: "og:type", content: resolved.schemaType === "BlogPosting" ? "article" : "website" });
        upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: "Container Specialist" });

        upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
        upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: resolved.twitterTitle || resolved.ogTitle });
        upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: resolved.twitterDescription || resolved.ogDescription });
        upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: resolved.twitterImage || resolved.ogImage });
    }, [seo, path]);
}

export default useSeo;
